// Passe de batalha do mês: nível e pontos, missões diárias/semanais, trilhas free e premium e as auras ganhas.
// CSS em client/css-parts/store.css (prefixo .pb-; o .bp- já existia em styles.css).
import { useEffect, useRef, useState } from 'preact/hooks';
import { EXERCISE_KINDS } from '../../../../shared/src/game/store';
import {
  BP_FREE, BP_LEVELS, BP_POINTS_PER_LEVEL, BP_PREMIUM, BP_PREMIUM_PRICE, DAILY_MISSIONS, MONSTER_MISSION_POINTS, WEEKLY_MISSIONS,
  auraById, bpLevel, bpRewardLabel, seasonAura, type BpReward, type BpState, type Mission,
} from '../../../../shared/src/game/battlepass';

const ICON: Record<string, number> = { stokens: 22516, gtokens: 22721, exercise: 28552, xpboost: 11510, bless: 3214, gold: 3043, coins: 22118, aura: 22771 };
const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const n0 = (v: number) => v.toLocaleString('pt-BR');
const hex = (c: number) => '#' + c.toString(16).padStart(6, '0');

export function BattlePass(props: {
  st: BpState | null; auras: string[]; aura: string | null; coins: number; monsters?: Array<{ name: string; goal: number; level: number }>;
  Img: (p: { typeId: number; count: number }) => preact.JSX.Element | null;
  onClaim: (track: 'free' | 'prem', level: number, potion?: string, exercise?: string) => void; onBuy: () => void; onAura: (id: string | null) => void;
}) {
  const [view, setView] = useState<'track' | 'missions'>('track');
  // como na compra e na recompensa diária: o jogador escolhe o tipo do item de treino e da poção
  const [ex, setEx] = useState(EXERCISE_KINDS[0]![0]);
  const potion = 'health';   // (as poções saíram das recompensas)
  const [help, setHelp] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const st = props.st;
  const lv = st ? bpLevel(st.points) : 0;
  // leva a trilha até o nível atual
  useEffect(() => {
    const el = trackRef.current; if (!el || view !== 'track') return;
    const cur = el.querySelector<HTMLElement>('.pb-col.next') ?? el.querySelector<HTMLElement>('.pb-col.cur');
    if (cur) el.scrollLeft = Math.max(0, cur.offsetLeft - el.clientWidth / 2 + cur.offsetWidth / 2);
  }, [view, lv, !!st]);
  if (!st) return <p class="muted small">Carregando...</p>;
  const into = lv >= BP_LEVELS ? BP_POINTS_PER_LEVEL : st.points % BP_POINTS_PER_LEVEL;
  const [y, m] = st.season.split('-').map(Number);
  const final = seasonAura(st.season);
  // dias até a virada do mês (horário de Brasília, como as chaves do passe)
  const br = new Date(Date.now() - 3 * 3600_000);
  const daysLeft = new Date(Date.UTC(br.getUTCFullYear(), br.getUTCMonth() + 1, 0)).getUTCDate() - br.getUTCDate() + 1;
  let claimable = 0;
  for (let l = 1; l <= lv; l++) {
    if (BP_FREE[l - 1] && !st.free.includes(l)) claimable++;
    if (st.premium && BP_PREMIUM[l - 1] && !st.prem.includes(l)) claimable++;
  }
  const lockedPrem = st.premium ? 0 : BP_PREMIUM.slice(0, lv).filter(Boolean).length;
  const showHelp = help || st.points === 0;
  const bar = (v: number, goal: number, done: boolean) => (
    <span class={'pb-mbar' + (done ? ' done' : '')}><i style={{ width: `${Math.min(100, (v / goal) * 100)}%` }} /></span>
  );
  const mission = (ms: BpState['daily'], x: Mission) => {
    const v = Math.min(x.goal, ms.progress[x.stat] ?? 0), done = ms.done.includes(x.id);
    return (
      <div key={x.id} class={'pb-mission' + (done ? ' done' : '')}>
        <span class="pb-mtxt">{done ? '✔ ' : ''}{x.text}</span>
        {bar(v, x.goal, done)}
        <span class="pb-mval">{done ? 'feito' : `${n0(v)}/${n0(x.goal)}`}</span>
        <span class="pb-pts">+{x.points}</span>
      </div>
    );
  };
  const cell = (r: BpReward | null, level: number, track: 'free' | 'prem') => {
    if (!r) return <div class="pb-cell empty"><small>—</small></div>;
    const got = (track === 'free' ? st.free : st.prem).includes(level);
    const can = !got && level <= lv && (track === 'free' || st.premium);
    const why = got ? 'Já pego' : can ? 'Disponível' : level > lv ? `Nível ${level}` : 'Só premium';
    return (
      <div class={'pb-cell ' + track + (got ? ' got' : can ? ' can' : ' lock') + (r.kind === 'aura' ? ' final' : '')} title={bpRewardLabel(r, st.season) + ' · ' + why}>
        <span class="item-slot">{r.kind === 'addon' ? <img class="store-img" src="/ui/btn-outfits-up.png" alt="" title="Addons" /> : r.kind === 'rtoken' ? <img class="store-img" src="/ui/btn-roleta-up.png" alt="" title="Ficha da Roleta" /> : (r.kind as string) === 'preycards' ? <img class="store-img" src="/assets/interface/images/prey-wildcards-icon.png" alt="" title="Prey Cards" /> : r.kind === 'fragments' ? <span class="rw-frag" title="Fragmentos de pedra de proteção">💠</span> : <props.Img typeId={ICON[r.kind]!} count={r.kind === 'coins' ? 100 : 1} />}</span>
        <span class="pb-l">{bpRewardLabel(r, st.season)}</span>
        {can && r.kind === 'exercise' && <select class="pb-sel" value={ex} onChange={(e) => setEx((e.target as HTMLSelectElement).value)}>{EXERCISE_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}
        {can ? <button class="tb-button green" onClick={() => props.onClaim(track, level, potion, ex)}>Pegar</button>
          : <span class="pb-st">{got ? '✔' : '🔒'} {got ? 'pego' : why}</span>}
      </div>
    );
  };
  return (
    <div class="pb">
      <div class="pb-head" style={{ '--pb-aura': hex(final.color) } as any}>
        <div class="pb-lvl" title="Nível do passe"><small>Nível</small><b>{lv}</b><small>de {BP_LEVELS}</small></div>
        <div class="pb-head-mid">
          <div class="pb-title">Passe de Batalha <span>· {MONTHS[(m ?? 1) - 1]} {y}</span>
            <button class={'pb-help' + (showHelp ? ' on' : '')} onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <div class="pb-xp">
            <span class="pb-bar"><i style={{ width: `${(into / BP_POINTS_PER_LEVEL) * 100}%` }} /></span>
            <span class="pb-xp-t">{lv >= BP_LEVELS ? 'Passe completo!' : <><b>{n0(into)}</b>/{n0(BP_POINTS_PER_LEVEL)} pontos para o nível {lv + 1}</>}</span>
          </div>
          <div class="pb-chips">
            <span class="pb-chip">⏳ {daysLeft === 1 ? 'último dia' : `${daysLeft} dias restantes`}</span>
            {claimable > 0 && <span class="pb-chip hot">🎁 {claimable} para pegar</span>}
          </div>
        </div>
        <div class={'pb-premium' + (st.premium ? ' on' : '')}>
          {st.premium ? <><b>👑 Premium</b><small>Trilha premium liberada</small></>
            : <><b>Premium</b>
              <small>{lockedPrem > 0 ? `${lockedPrem} recompensas já esperando` : 'Libera a trilha dourada'}</small>
              <button class="tb-button green" disabled={props.coins < BP_PREMIUM_PRICE} onClick={props.onBuy}><i class="st-coin" />{BP_PREMIUM_PRICE} coins</button></>}
        </div>
      </div>
      <div class="pb-final">
        <span class="pb-orb" style={{ '--pb-aura': hex(final.color) } as any} aria-hidden="true" />
        <span>Recompensa final premium desta temporada: <b style={{ color: hex(final.color) }}>{final.name}</b>, exclusiva deste mês.
          <small> As recompensas vão para a Store Box. As poções e o item de treino seguem a sua vocation.</small></span>
      </div>
      {showHelp && (
        <div class="pb-steps">
          <div><i>⚔️</i><b>1. Ganhe pontos</b><small>Cumpra missões diárias, semanais e de monstro. A cada {n0(BP_POINTS_PER_LEVEL)} pontos você sobe um nível.</small></div>
          <div><i>🎁</i><b>2. Pegue</b><small>Cada nível libera a recompensa da trilha free. Clique em "Pegar".</small></div>
          <div><i>👑</i><b>3. Premium</b><small>Com o premium, a trilha dourada também libera, até a aura do mês no nível {BP_LEVELS}.</small></div>
        </div>
      )}
      <div class="pb-nav">
        <button class={'tb-button' + (view === 'track' ? ' green' : '')} onClick={() => setView('track')}>Recompensas{claimable > 0 ? ` (${claimable})` : ''}</button>
        <button class={'tb-button' + (view === 'missions' ? ' green' : '')} onClick={() => setView('missions')}>Missões</button>
      </div>
      {view === 'track' ? (
        <div class="pb-trackw">
          <div class="pb-labels"><span class="free">Free</span><span class="lv">Nível</span><span class="prem">👑 Premium</span></div>
          <div class="pb-track" ref={trackRef}>
            {Array.from({ length: BP_LEVELS }, (_, i) => (
              <div key={i} class={'pb-col' + (i + 1 <= lv ? ' reached' : '') + (i + 1 === lv ? ' cur' : '') + (i === lv ? ' next' : '')}>
                {cell(BP_FREE[i] ?? null, i + 1, 'free')}
                <span class="pb-node"><b>{i + 1}</b></span>
                {cell(BP_PREMIUM[i]!, i + 1, 'prem')}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div class="pb-missions">
          <h4 class="pb-h">☀️ Diárias <small>renovam à meia-noite</small></h4>
          {DAILY_MISSIONS.map((x) => mission(st.daily, x))}
          <h4 class="pb-h">📅 Semanais <small>renovam na segunda-feira</small></h4>
          {WEEKLY_MISSIONS.map((x) => mission(st.weekly, x))}
          <h4 class="pb-h">🐉 Missões de monstro <small>cada uma vale uma vez por temporada · +{MONSTER_MISSION_POINTS} pontos</small></h4>
          <p class="pb-note">Toda criatura das hunts tem a sua: mate a quantidade pedida (1.000 nas hunts até level 100, 2.000 de 100 a 199, e assim por diante). Concluídas nesta temporada: <b>{st.mdone?.length ?? 0}</b>.</p>
          {(props.monsters ?? []).map((x) => {
            const done = !!st.mdone?.includes(x.name), v = done ? x.goal : Math.min(x.goal, st.mkills?.[x.name] ?? 0);
            return (
              <div key={x.name} class={'pb-mission' + (done ? ' done' : '')}>
                <span class="pb-mtxt">{done ? '✔ ' : ''}Mate {n0(x.goal)} {x.name} <small>(hunt level {x.level})</small></span>
                {bar(v, x.goal, done)}
                <span class="pb-mval">{done ? 'feito' : `${n0(v)}/${n0(x.goal)}`}</span>
              </div>
            );
          })}
        </div>
      )}
      {props.auras.length > 0 && (
        <div class="pb-aura">
          <span class="pb-orb sm" style={{ '--pb-aura': hex(auraById(props.aura ?? undefined)?.color ?? 0x444444) } as any} aria-hidden="true" />
          <b>Aura em uso</b>
          <select class="pb-sel" value={props.aura ?? ''} onChange={(e) => props.onAura((e.target as HTMLSelectElement).value || null)}>
            <option value="">Nenhuma</option>
            {props.auras.map((a) => <option key={a} value={a}>{auraById(a)?.name ?? a}</option>)}
          </select>
          <small>{props.auras.length} {props.auras.length === 1 ? 'aura ganha' : 'auras ganhas'}</small>
        </div>
      )}
    </div>
  );
}
